import { useMemo } from "react";

// Common country codes shown in the dropdown. India first, then everywhere our
// user base is likely to sign up from. Feel free to add more — the "iso" is only
// used as a React key.
const COUNTRIES = [
  { iso: "IN", code: "91", flag: "🇮🇳", label: "India", minLen: 10, maxLen: 10 },
  { iso: "US", code: "1", flag: "🇺🇸", label: "United States", minLen: 10, maxLen: 10 },
  { iso: "GB", code: "44", flag: "🇬🇧", label: "United Kingdom", minLen: 7, maxLen: 11 },
  { iso: "AE", code: "971", flag: "🇦🇪", label: "United Arab Emirates", minLen: 8, maxLen: 9 },
  { iso: "SA", code: "966", flag: "🇸🇦", label: "Saudi Arabia", minLen: 9, maxLen: 9 },
  { iso: "SG", code: "65", flag: "🇸🇬", label: "Singapore", minLen: 8, maxLen: 8 },
  { iso: "MY", code: "60", flag: "🇲🇾", label: "Malaysia", minLen: 9, maxLen: 10 },
  { iso: "AU", code: "61", flag: "🇦🇺", label: "Australia", minLen: 9, maxLen: 9 },
  { iso: "CA", code: "1", flag: "🇨🇦", label: "Canada", minLen: 10, maxLen: 10 },
  { iso: "NP", code: "977", flag: "🇳🇵", label: "Nepal", minLen: 10, maxLen: 10 },
  { iso: "LK", code: "94", flag: "🇱🇰", label: "Sri Lanka", minLen: 9, maxLen: 9 },
  { iso: "BD", code: "880", flag: "🇧🇩", label: "Bangladesh", minLen: 10, maxLen: 10 },
];

export const DEFAULT_COUNTRY = COUNTRIES[0]; // India

export const countryByIso = (iso) => COUNTRIES.find((c) => c.iso === iso) || DEFAULT_COUNTRY;

export const buildE164 = (iso, digits) => {
  const c = countryByIso(iso);
  return `+${c.code}${digits}`;
};

export const isValidPhone = (iso, digits) => {
  const c = countryByIso(iso);
  const n = (digits || "").length;
  return n >= c.minLen && n <= c.maxLen;
};

export const formatPhoneDisplay = (iso, digits) => {
  const c = countryByIso(iso);
  return digits ? `+${c.code} ${digits}` : `+${c.code}`;
};

/**
 * Split-country-code phone input. Emits raw local digits via `onDigits` and the
 * chosen country ISO via `onCountry`. The parent composes the final E.164 number
 * with `buildE164(iso, digits)` before sending it to the OTP endpoints.
 */
export const PhoneInput = ({
  label,
  iso,
  onCountry,
  digits,
  onDigits,
  testIdPrefix = "phone",
  autoFocus = false,
}) => {
  const c = useMemo(() => countryByIso(iso), [iso]);
  const handleDigits = (e) => {
    const cleaned = e.target.value.replace(/\D/g, "").slice(0, c.maxLen);
    onDigits(cleaned);
  };
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-1 flex gap-0 border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-[#1A365D]">
        <select
          data-testid={`${testIdPrefix}-country`}
          value={iso}
          onChange={(e) => {
            onCountry(e.target.value);
            // Trim digits down to the new country's cap when switching.
            const nextMax = countryByIso(e.target.value).maxLen;
            if (digits.length > nextMax) onDigits(digits.slice(0, nextMax));
          }}
          className="h-11 min-w-[92px] border-r border-slate-200 bg-slate-50 px-2 text-sm focus:outline-none"
        >
          {COUNTRIES.map((opt) => (
            <option key={`${opt.iso}-${opt.code}`} value={opt.iso} title={opt.label}>
              {opt.flag} +{opt.code}
            </option>
          ))}
        </select>
        <input
          data-testid={`${testIdPrefix}-input`}
          type="tel"
          inputMode="numeric"
          value={digits}
          onChange={handleDigits}
          placeholder={c.iso === "IN" ? "9876543210" : "Mobile number"}
          autoFocus={autoFocus}
          className="h-11 w-full flex-1 border-0 bg-white px-3 font-mono-num text-sm focus:outline-none"
        />
      </div>
      <p className="mt-1 text-[11px] text-slate-400">
        {c.iso === "IN"
          ? "Enter your 10-digit mobile number without the country code."
          : `Enter a valid ${c.label} number (${c.minLen}${c.minLen === c.maxLen ? "" : `–${c.maxLen}`} digits).`}
      </p>
    </label>
  );
};
